import React, { useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useTranslation } from "react-i18next";
import {
  TIMELINE_FILTERS,
  TIMELINE_LAYER_FILTERS,
  TIMELINE_CONVERSATION_FACETS,
  MASTER_TIMELINE_FILTERS,
  TIMELINE_FILTER_PRESETS,
  resolveTimelineFilterPreset,
} from "./messageTypeRegistry";

const FILTER_LABEL_KEYS = {
  [TIMELINE_FILTERS.CONVERSATION]: "timeline.filterConversation",
  [TIMELINE_FILTERS.INTERNAL]: "timeline.filterInternal",
  [TIMELINE_FILTERS.ACTIVITY]: "timeline.filterActivity",
  [TIMELINE_FILTERS.ACCESS]: "timeline.filterAccess",
  [TIMELINE_FILTERS.MEDIA]: "timeline.filterMedia",
  [TIMELINE_FILTERS.EMAIL]: "timeline.filterEmail",
  [TIMELINE_FILTERS.BOT]: "timeline.filterBot",
  [TIMELINE_FILTERS.SURVEY]: "timeline.filterSurvey",
  [TIMELINE_FILTERS.API_LOGS]: "timeline.filterApiLogs",
  [TIMELINE_FILTERS.AI_LOGS]: "timeline.filterAiLogs",
};

const FILTER_DESC_KEYS = {
  [TIMELINE_FILTERS.CONVERSATION]: "timeline.filterConversationDesc",
  [TIMELINE_FILTERS.INTERNAL]: "timeline.filterInternalDesc",
  [TIMELINE_FILTERS.ACTIVITY]: "timeline.filterActivityDesc",
  [TIMELINE_FILTERS.ACCESS]: "timeline.filterAccessDesc",
  [TIMELINE_FILTERS.MEDIA]: "timeline.filterMediaDesc",
  [TIMELINE_FILTERS.EMAIL]: "timeline.filterEmailDesc",
  [TIMELINE_FILTERS.BOT]: "timeline.filterBotDesc",
  [TIMELINE_FILTERS.SURVEY]: "timeline.filterSurveyDesc",
  [TIMELINE_FILTERS.API_LOGS]: "timeline.filterApiLogsDesc",
  [TIMELINE_FILTERS.AI_LOGS]: "timeline.filterAiLogsDesc",
};

const FILTER_DEFAULTS = {
  [TIMELINE_FILTERS.CONVERSATION]: "Conversa",
  [TIMELINE_FILTERS.INTERNAL]: "Anotações internas",
  [TIMELINE_FILTERS.ACTIVITY]: "Atividade",
  [TIMELINE_FILTERS.ACCESS]: "Acessos (LGPD)",
  [TIMELINE_FILTERS.MEDIA]: "Mídia",
  [TIMELINE_FILTERS.EMAIL]: "E-mail",
  [TIMELINE_FILTERS.BOT]: "Bot / automático",
  [TIMELINE_FILTERS.SURVEY]: "Pesquisas",
  [TIMELINE_FILTERS.API_LOGS]: "Logs de API",
  [TIMELINE_FILTERS.AI_LOGS]: "Logs de IA",
};

const FILTER_DESC_DEFAULTS = {
  [TIMELINE_FILTERS.CONVERSATION]: "Mensagens nos canais com o cliente",
  [TIMELINE_FILTERS.INTERNAL]: "Notas visíveis só para a equipe",
  [TIMELINE_FILTERS.ACTIVITY]: "Transferências, status e eventos (sem logs de API/IA)",
  [TIMELINE_FILTERS.ACCESS]: "Quem abriu a solicitação e o que havia no histórico. Recarregar ou voltar sem dado novo não conta",
  [TIMELINE_FILTERS.MEDIA]: "Imagens, áudio, vídeo, arquivos, localização e contatos",
  [TIMELINE_FILTERS.EMAIL]: "Mensagens enviadas ou recebidas por e-mail",
  [TIMELINE_FILTERS.BOT]: "Chatbot, respostas automáticas e agente de IA ao cliente",
  [TIMELINE_FILTERS.SURVEY]: "Pesquisas enviadas e respostas registradas",
  [TIMELINE_FILTERS.API_LOGS]:
    "Gupshup, TotalBus, Praxio, Vuxx, Cielo, Rede e demais gateways (request/response)",
  [TIMELINE_FILTERS.AI_LOGS]: "OpenAI, regras do chatbot e agentes de IA (audit técnico)",
};

const PRESET_LABEL_KEYS = {
  [TIMELINE_FILTER_PRESETS.EVERYTHING]: "timeline.presetEverything",
  [TIMELINE_FILTER_PRESETS.CUSTOMER_ONLY]: "timeline.presetCustomerOnly",
  [TIMELINE_FILTER_PRESETS.WORK]: "timeline.presetWork",
  [TIMELINE_FILTER_PRESETS.HIDE_TECHNICAL]: "timeline.presetHideTechnical",
  [TIMELINE_FILTER_PRESETS.DIAGNOSTIC]: "timeline.presetDiagnostic",
};

const PRESET_DEFAULTS = {
  [TIMELINE_FILTER_PRESETS.EVERYTHING]: "Ver tudo",
  [TIMELINE_FILTER_PRESETS.CUSTOMER_ONLY]: "Só conversa com o cliente",
  [TIMELINE_FILTER_PRESETS.WORK]: "Atendimento (conversa + notas + atividade)",
  [TIMELINE_FILTER_PRESETS.HIDE_TECHNICAL]: "Ocultar logs técnicos",
  [TIMELINE_FILTER_PRESETS.DIAGNOSTIC]: "Só diagnóstico (API + IA)",
};

const menuItemSx = {
  alignItems: "flex-start",
  py: 1,
  whiteSpace: "normal",
  overflow: "visible",
};

const listItemTextSx = {
  m: 0,
  pr: 0.5,
  flex: "1 1 auto",
  minWidth: 0,
};

const subheaderSx = {
  lineHeight: 1.4,
  py: 0.75,
  bgcolor: "background.paper",
  fontSize: "0.68rem",
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: "uppercase",
  color: "text.secondary",
};

const sameFilterSet = (a, b) => {
  const sa = [...(a || [])].sort().join(",");
  const sb = [...(b || [])].sort().join(",");
  return sa === sb;
};

const TimelineFilterBar = ({ value = [], onChange, showApiLogsFilter = false }) => {
  const { t } = useTranslation("chat");
  const [anchorEl, setAnchorEl] = useState(null);

  const activeSet = new Set(Array.isArray(value) ? value : []);
  const isFiltered = activeSet.size > 0;

  const getLabel = (id) => {
    const key = FILTER_LABEL_KEYS[id];
    return t(key, FILTER_DEFAULTS[id] || id);
  };

  const getDescription = (id) => {
    const key = FILTER_DESC_KEYS[id];
    return t(key, FILTER_DESC_DEFAULTS[id] || "");
  };

  const getPresetLabel = (id) => t(PRESET_LABEL_KEYS[id], PRESET_DEFAULTS[id] || id);

  const toggleFilter = (id) => {
    const next = new Set(activeSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange([...next]);
  };

  const removeFilter = (id) => {
    onChange([...activeSet].filter((f) => f !== id));
  };

  const applyPreset = (presetId) => {
    const next = resolveTimelineFilterPreset(presetId, {
      canViewApiAudit: showApiLogsFilter,
    });
    if (next == null) return;
    onChange(next);
    setAnchorEl(null);
  };

  const clearAll = () => {
    onChange([]);
    setAnchorEl(null);
  };

  const presets = showApiLogsFilter
    ? [
        TIMELINE_FILTER_PRESETS.EVERYTHING,
        TIMELINE_FILTER_PRESETS.CUSTOMER_ONLY,
        TIMELINE_FILTER_PRESETS.HIDE_TECHNICAL,
        TIMELINE_FILTER_PRESETS.DIAGNOSTIC,
      ]
    : [
        TIMELINE_FILTER_PRESETS.EVERYTHING,
        TIMELINE_FILTER_PRESETS.CUSTOMER_ONLY,
        TIMELINE_FILTER_PRESETS.WORK,
      ];

  const chipOrder = [
    ...TIMELINE_LAYER_FILTERS,
    ...TIMELINE_CONVERSATION_FACETS,
    ...(showApiLogsFilter ? MASTER_TIMELINE_FILTERS : []),
  ];
  const activeChips = chipOrder.filter((id) => activeSet.has(id));

  const renderFilterItem = (id) => (
    <MenuItem key={id} dense onClick={() => toggleFilter(id)} sx={menuItemSx}>
      <ListItemIcon sx={{ minWidth: 32, mt: 0.25, flexShrink: 0 }}>
        <Checkbox
          edge="start"
          checked={activeSet.has(id)}
          tabIndex={-1}
          disableRipple
          size="small"
        />
      </ListItemIcon>
      <ListItemText
        sx={listItemTextSx}
        primary={getLabel(id)}
        secondary={getDescription(id)}
        primaryTypographyProps={{
          fontSize: "0.85rem",
          fontWeight: 500,
          sx: { whiteSpace: "normal" },
        }}
        secondaryTypographyProps={{
          fontSize: "0.72rem",
          lineHeight: 1.4,
          sx: { whiteSpace: "normal", display: "block" },
        }}
      />
    </MenuItem>
  );

  return (
    <Box
      sx={(theme) => ({
        position: "absolute",
        top: 6,
        right: 8,
        zIndex: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 0.5,
        flexWrap: "wrap",
        maxWidth: "min(480px, 62%)",
        px: 0.5,
        py: 0.25,
        borderRadius: 1.5,
        backgroundColor:
          theme.palette.mode === "light"
            ? "rgba(255, 255, 255, 0.88)"
            : "rgba(18, 18, 18, 0.82)",
        backdropFilter: "blur(6px)",
        boxShadow: theme.shadows[1],
        border: `1px solid ${theme.palette.divider}`,
      })}
    >
      {activeChips.map((id) => (
        <Chip
          key={id}
          label={getLabel(id)}
          size="small"
          onDelete={() => removeFilter(id)}
          sx={{ height: 22, fontSize: "0.68rem", maxWidth: 160 }}
        />
      ))}
      <Tooltip title={t("timeline.filterMenu", "Filtrar mensagens")}>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          color={isFiltered ? "primary" : "default"}
          sx={{ p: 0.5 }}
          aria-label={t("timeline.filterMenu", "Filtrar mensagens")}
        >
          <FilterListIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: 320,
              maxWidth: "calc(100vw - 24px)",
              maxHeight: "min(70vh, 560px)",
              "& .MuiMenuItem-root": menuItemSx,
            },
          },
        }}
      >
        <ListSubheader disableSticky sx={subheaderSx}>
          {t("timeline.sectionPresets", "Atalhos")}
        </ListSubheader>
        {presets.map((presetId) => {
          const resolved = resolveTimelineFilterPreset(presetId, {
            canViewApiAudit: showApiLogsFilter,
          });
          const selected = resolved != null && sameFilterSet(value, resolved);
          return (
            <MenuItem
              key={presetId}
              dense
              selected={selected}
              onClick={() => applyPreset(presetId)}
              sx={menuItemSx}
            >
              <ListItemText
                sx={listItemTextSx}
                primary={getPresetLabel(presetId)}
                primaryTypographyProps={{
                  fontSize: "0.85rem",
                  fontWeight: selected ? 600 : 500,
                }}
              />
            </MenuItem>
          );
        })}

        <Divider sx={{ my: 0.5 }} />
        <ListSubheader disableSticky sx={subheaderSx}>
          {t("timeline.sectionLayers", "Camadas")}
        </ListSubheader>
        {TIMELINE_LAYER_FILTERS.map(renderFilterItem)}

        <Divider sx={{ my: 0.5 }} />
        <ListSubheader disableSticky sx={subheaderSx}>
          {t("timeline.sectionFacets", "Detalhes da conversa")}
        </ListSubheader>
        <Box sx={{ px: 2, pb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.35 }}>
            {t(
              "timeline.sectionFacetsHint",
              "Com “Conversa” marcado, estes opções restringem o que aparece. Sozinhas, mostram só aquele tipo."
            )}
          </Typography>
        </Box>
        {TIMELINE_CONVERSATION_FACETS.map(renderFilterItem)}

        {showApiLogsFilter && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <ListSubheader disableSticky sx={subheaderSx}>
              {t("timeline.sectionMaster", "Diagnóstico (master)")}
            </ListSubheader>
            {MASTER_TIMELINE_FILTERS.map(renderFilterItem)}
          </>
        )}

        {isFiltered && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem dense onClick={clearAll} sx={menuItemSx}>
              <ListItemText
                sx={listItemTextSx}
                primary={t("timeline.filterClearAll", "Limpar filtros")}
                primaryTypographyProps={{ fontSize: "0.85rem", color: "text.secondary" }}
              />
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default TimelineFilterBar;
